import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { ClipLoader } from "react-spinners";
import IoSearch from "../../assets/icons/search";
import { storiesRoute } from "../../routes/routesPath";
import useDebounce from "../../hooks/useDebounce";
import { sortingOptions } from "../../utils/constants";
import { getFilters } from "../../utils/helpers";
import { mainStoriesListingFilters } from "../../utils/filters";
import { isArray } from "../../utils/formatersAndParsers";
import { handleApiRequest } from "../../services/handleApiRequest";
import { getMainStoriesList } from "../../redux/stories/thunk";
import LoadIndicator from "../../Component/Loader";
import StoryCard from "../../Component/storyCard/mainStoryCard";
import CardSkeleton from "../../Component/skeleton/cardSkeleton";
import FeelingsFilter from "../../Component/common/filters/feelingsFilter";
import NoDataFound from "../../Component/common/noDataFound";
import SortingDropdown from "../../Component/common/filters/sortingDropdown";
import MyHelmet from "../../seo/helmet";

export default function Stories() {
  const debounce = useDebounce();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFilters = Object.fromEntries(searchParams.entries());

  const { storiesList = {}, loadingStoriesList = false } = useSelector(
    (state) => state.stories || {}
  );

  const [showReaction, setShowReaction] = useState("");
  const [searchValue, setSearchValue] = useState(selectedFilters.search || "");

  const handleSearch = useCallback(
    debounce((e) => {
      const { value, name } = e.target;
      setSearchParams(
        (prev) => {
          prev.set(name, value);
          return prev;
        },
        { replace: true }
      );
    }, 1500),
    []
  );

  const handleStories = async () => {
    const filters = await getFilters(mainStoriesListingFilters, selectedFilters);

    await handleApiRequest(getMainStoriesList, filters);
  };

  useEffect(() => {
    handleStories();
  }, [searchParams]);

  return (
    <>
      <MyHelmet
        title={"Read what others are feeling"}
        keywords={["Read life stories"]}
        description="FeelingHub is a platform to share feelings online, read confessions, life stories and problems of others. Share your thoughts on their experiences and connect!"
        link={storiesRoute}
      />

      <section className="storiesListContainer">
        <Row className="m-0">
          <Col lg={8} className="p-0">
            <div className="bg-header me-lg-5">
              {isArray(storiesList.records).length > 0 && (
                <div
                  className={`d-flex align-items-center justify-content-center ${
                    loadingStoriesList ? "" : "hide-loader"
                  }`}>
                  <ClipLoader
                    color={"var(--primary-color)"}
                    cssOverride={{ borderWidth: 5 }}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                </div>
              )}

              <div className="storyFilters d-block d-sm-flex align-items-center justify-content-between p-3 pb-2 ps-lg-5 mb-3">
                <div className="ms-sm-2 position-relative d-none d-sm-block">
                  <input
                    type="text"
                    className="bg-transparent border-primary rounded-pill form-control shadow-none"
                    id="search"
                    placeholder="Search"
                    name="search"
                    value={searchValue}
                    onChange={(e) => {
                      const { value } = e.target;
                      setSearchValue(value);
                      handleSearch(e);
                    }}
                  />
                  <IoSearch className="searchIcon position-absolute end-0 top-50" />
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center ">
                    <FeelingsFilter className="pe-2" />
                    <SortingDropdown sortingOptions={sortingOptions} />
                  </div>
                  <span
                    className="d-inline d-sm-none"
                    data-bs-toggle="collapse"
                    data-bs-target="#searchBox"
                    aria-expanded="false"
                    aria-controls="searchBox">
                    <IoSearch className="width-22 height-22 text-primary" />
                  </span>
                </div>
                <div className="collapse d-sm-none" id="searchBox">
                  <input
                    type="text"
                    className="bg-transparent border-primary rounded-pill form-control shadow-none"
                    id="search"
                    placeholder="Search"
                    name="search"
                    value={searchValue}
                    onChange={(e) => {
                      const { value } = e.target;
                      setSearchValue(value);
                      handleSearch(e);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={`px-2 px-lg-5`}>
              {storiesList.totalCount === 0 ? (
                <NoDataFound />
              ) : storiesList.records ? (
                <InfiniteScroll
                  dataLength={storiesList.records?.length}
                  next={() =>
                    setSearchParams(
                      (prev) => {
                        const prevPage =
                          selectedFilters.page && !isNaN(selectedFilters.page)
                            ? Number(selectedFilters.page)
                            : 1;
                        prev.set("page", prevPage + 1);

                        return prev;
                      },
                      { replace: true }
                    )
                  }
                  hasMore={storiesList.records?.length < storiesList.totalCount}
                  loader={
                    <div className="text-center my-4">
                      <ClipLoader
                        color={"var(--primary-color)"}
                        cssOverride={{ borderWidth: 5 }}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                    </div>
                  }
                  scrollableTarget="scrollableDiv"
                  endMessage={
                    <p className="text-center">
                      <b>Yay! You have seen it all</b>
                    </p>
                  }>
                  {isArray(storiesList.records).map((story, i) => (
                    <StoryCard
                      key={story._id}
                      story={story}
                      showReaction={showReaction}
                      setShowReaction={setShowReaction}
                    />
                  ))}
                </InfiniteScroll>
              ) : (
                Array.from({ length: 3 }).map((_, index) => <CardSkeleton key={index} />)
              )}
            </div>
          </Col>
          <Col className="d-none d-lg-block col-lg-4 py-2">
            <div className="shadow h-100 d-flex align-items-center justify-content-center my-3 rounded">
              Google Ad
            </div>
          </Col>
        </Row>

        {/* <div className="curvatureContainer lowerCurvatureContainer">
          <div>
            <div id="curved-corner-bottomright" className="curvedCorner" />
          </div>
          <div>
            <div id="curved-corner-bottomleft" className="curvedCorner" />
          </div>
        </div> */}
      </section>
    </>
  );
}
